import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Play, Loader2, CheckCircle2, XCircle,
  AlertTriangle, Clock, Database, ScanLine, Sparkles, Trash2, RefreshCw,
  ChevronDown, ChevronRight,
} from "lucide-react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { API } from "../config/api";
import ProjectIdBackfillCard from "../components/data_import/ProjectIdBackfillCard";
import VisitSynthesisBackfillCard from "../components/data_import/VisitSynthesisBackfillCard";
import TaskImportCard from "../components/data_import/TaskImportCard";

const ENTITY_TYPES = [
  { key: "leads", label: "Leads", desc: "LeadSquared opportunity export (Wave 1)" },
  { key: "activities", label: "Activities", desc: "Activity Trend Report → interactions" },
  { key: "stage_history", label: "Stage History", desc: "Stage Change Report → lead_stage_history" },
  { key: "campaigns", label: "Campaigns", desc: "Campaign Report → campaign master + lead attribution" },
  { key: "ai_recalibrate", label: "AI Recalibrate", desc: "Re-score leads, refresh revival/stage predictions, aggregate campaign performance, mark for lazy AI refresh" },
  { key: "backfill_project", label: "Backfill Project", desc: "Propagate leads' current project to their historical site visits + tasks. Run once after the Feb 2026 bulk-set-project fix; idempotent." },
];

// Phases available per entity. Wave 2 entities only support analyze/dry-run/import.
const PHASES_BY_ENTITY = {
  leads: ["analyze", "dry-run", "import", "cleanup", "wipe-and-import"],
  activities: ["dry-run", "import"],
  stage_history: ["dry-run", "import"],
  campaigns: ["dry-run", "import"],
  ai_recalibrate: ["recalibrate"],
  backfill_project: ["dry-run-backfill", "apply-backfill"],
};

const PHASES = [
  {
    key: "analyze",
    label: "Analyze",
    desc: "Read-only — show row counts, unmapped owners/projects/sources",
    icon: ScanLine, color: "bg-sky-600 hover:bg-sky-700",
    needsFile: true,
  },
  {
    key: "dry-run",
    label: "Dry Run",
    desc: "Simulate the full import — preview inserts vs updates vs duplicates. No DB writes.",
    icon: Sparkles, color: "bg-indigo-600 hover:bg-indigo-700",
    needsFile: true,
  },
  {
    key: "import",
    label: "Import",
    desc: "Commit the import. Updates existing leads (by leadsquared_id) + inserts new ones. Auto-runs cleanup + score sync.",
    icon: Play, color: "bg-emerald-600 hover:bg-emerald-700",
    needsFile: true,
  },
  {
    key: "cleanup",
    label: "Cleanup only",
    desc: "Dedupe race-condition rows + sync score buckets (no file needed).",
    icon: RefreshCw, color: "bg-slate-700 hover:bg-slate-800",
    needsFile: false,
  },
  {
    key: "wipe-and-import",
    label: "Wipe & Reimport",
    desc: "DELETE all leads/interactions/tasks/visits, then import fresh. Requires typed 'WIPE' confirmation.",
    icon: Trash2, color: "bg-red-600 hover:bg-red-700",
    needsFile: true,
    danger: true,
  },
  {
    key: "recalibrate",
    label: "Recalibrate AI",
    desc: "Re-score all leads · refresh revival ranking on lost leads · derive stage-transition predictions from history · aggregate campaign performance · mark for lazy AI refresh. Rule-based, no LLM cost. ~5-10 min.",
    icon: Sparkles, color: "bg-violet-600 hover:bg-violet-700",
    needsFile: false,
  },
  {
    key: "dry-run-backfill",
    label: "Dry Run Backfill",
    desc: "Count how many historical site_visits + tasks would have their project field aligned to the lead's current project. No writes. Run this first.",
    icon: ScanLine, color: "bg-sky-600 hover:bg-sky-700",
    needsFile: false,
  },
  {
    key: "apply-backfill",
    label: "Apply Backfill",
    desc: "Propagate every lead's current project to its child site_visits + tasks where they don't match. Idempotent — safe to re-run. ~1-2 min for 100k child docs.",
    icon: Play, color: "bg-emerald-600 hover:bg-emerald-700",
    needsFile: false,
  },
];

const STATUS_BADGE = {
  pending: { cls: "bg-slate-100 text-slate-700", icon: Clock, label: "Pending" },
  running: { cls: "bg-blue-100 text-blue-700", icon: Loader2, label: "Running", spin: true },
  completed: { cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Completed" },
  failed: { cls: "bg-red-100 text-red-700", icon: XCircle, label: "Failed" },
  cancelled: { cls: "bg-amber-100 text-amber-700", icon: AlertTriangle, label: "Cancelled" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}
      data-testid={`job-status-${status}`}
    >
      <Icon className={`w-3 h-3 ${cfg.spin ? "animate-spin" : ""}`} />
      {cfg.label}
    </span>
  );
}

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1_048_576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1_048_576).toFixed(2)} MB`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function DataImportPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [openPhase, setOpenPhase] = useState(null);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [skipExisting, setSkipExisting] = useState(false);
  const [entityType, setEntityType] = useState("leads");
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const logEndRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/import-jobs?limit=25`);
      setJobs(res.data.jobs || []);
    } catch (e) {
      console.error("fetch jobs error", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobDetail = useCallback(async (jobId) => {
    try {
      const res = await axios.get(`${API}/admin/import-jobs/${jobId}`);
      setSelectedJob(res.data);
      if (logEndRef.current) {
        logEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    } catch (e) {
      console.error("fetch job detail error", e);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll active job + jobs list while anything is running
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const anyRunning = jobs.some(j => j.status === "running" || j.status === "pending");
    const selRunning = selectedJob && (selectedJob.status === "running" || selectedJob.status === "pending");
    if (anyRunning || selRunning) {
      pollRef.current = setInterval(() => {
        fetchJobs();
        if (selectedJob?.job_id) fetchJobDetail(selectedJob.job_id);
      }, 2000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobs, selectedJob, fetchJobs, fetchJobDetail]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const openPhaseDialog = (phase) => {
    setOpenPhase(phase);
    setFile(null);
    setWipeConfirm("");
    setSkipExisting(false);
  };

  const closePhaseDialog = () => {
    setOpenPhase(null);
    setFile(null);
    setWipeConfirm("");
    setSkipExisting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitJob = async () => {
    if (!openPhase) return;
    if (openPhase.needsFile && !file) {
      toast.error("Please choose an Excel file");
      return;
    }
    if (openPhase.key === "wipe-and-import" && wipeConfirm !== "WIPE") {
      toast.error("Type WIPE in the confirmation box");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      if (file) form.append("file", file);
      form.append("entity_type", entityType);
      form.append("phase", openPhase.key);
      if (openPhase.key === "wipe-and-import") form.append("wipe_confirm", "WIPE");
      if (skipExisting && entityType === "leads" && (openPhase.key === "import" || openPhase.key === "dry-run")) {
        form.append("skip_existing", "true");
      }

      const res = await axios.post(`${API}/admin/import-jobs`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Job queued (${res.data.job_id}) — running in background`);
      closePhaseDialog();
      await fetchJobs();
      await fetchJobDetail(res.data.job_id);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to start job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="data-import-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Data Import</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Bulk-import leads from LeadSquared (or any compatible Excel). Each run is logged and auditable.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchJobs} data-testid="refresh-jobs-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Hot-fix backfill card (Feb 2026 import bug) */}
        <ProjectIdBackfillCard />

        {/* Hot-fix Path A: synthesise missing site_visits records for orphan SV-stage leads */}
        <VisitSynthesisBackfillCard />

        {/* LeadSquared Task Trend Report import (Feb 2026) */}
        <TaskImportCard />

        {/* Phase action cards */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900">Start a new import</CardTitle>
            <div className="flex flex-wrap gap-2 pt-2" data-testid="entity-tabs">
              {ENTITY_TYPES.map(e => (
                <button
                  key={e.key}
                  onClick={() => setEntityType(e.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${entityType === e.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`entity-tab-${e.key}`}
                  title={e.desc}
                >
                  {e.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {ENTITY_TYPES.find(e => e.key === entityType)?.desc}
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {PHASES.filter(p => (PHASES_BY_ENTITY[entityType] || []).includes(p.key)).map(p => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.key}
                    onClick={() => openPhaseDialog(p)}
                    className={`text-left rounded-lg border p-4 hover:shadow-md transition-shadow ${p.danger ? "border-red-200 bg-red-50/30 hover:border-red-300" : "border-slate-200 bg-white hover:border-slate-400"}`}
                    data-testid={`phase-btn-${p.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white ${p.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900 text-sm">{p.label}</p>
                          {p.danger && <Badge variant="destructive" className="text-[10px] h-4 px-1.5">Danger</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Active job detail */}
        {selectedJob && (
          <Card className="bg-slate-900 text-white border-slate-800" data-testid="active-job-card">
            <CardHeader className="pb-3 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    {selectedJob.entity_type} · {selectedJob.phase}
                  </CardTitle>
                  <StatusBadge status={selectedJob.status} />
                </div>
                <button onClick={() => setSelectedJob(null)} className="text-slate-400 hover:text-white text-xs" data-testid="close-job-detail-btn">
                  Close
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {/* Progress bar */}
              <div>
                <div className="flex items-center justify-between text-xs text-slate-300 mb-1">
                  <span>{selectedJob.progress_message || "—"}</span>
                  <span>{selectedJob.progress_pct ?? 0}%</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${selectedJob.status === "failed" ? "bg-red-500" : "bg-emerald-500"}`}
                    style={{ width: `${selectedJob.progress_pct ?? 0}%` }}
                  />
                </div>
              </div>

              {/* File + meta */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="text-slate-400">File</p>
                  <p className="font-medium truncate" title={selectedJob.file_name}>{selectedJob.file_name || "—"}</p>
                </div>
                <div>
                  <p className="text-slate-400">Size</p>
                  <p className="font-medium">{fmtBytes(selectedJob.file_size_bytes)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Started</p>
                  <p className="font-medium">{fmtTime(selectedJob.started_at || selectedJob.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-400">By</p>
                  <p className="font-medium truncate">{selectedJob.created_by?.name || "—"}</p>
                </div>
              </div>

              {/* Error */}
              {selectedJob.error && (
                <div className="rounded-md bg-red-900/40 border border-red-700 px-3 py-2 text-xs text-red-200">
                  <strong>Error:</strong> {selectedJob.error}
                </div>
              )}

              {/* Final counts */}
              {selectedJob.report?.final_counts && (
                <div className="rounded-md bg-emerald-900/30 border border-emerald-700/60 px-3 py-2 text-xs text-emerald-200">
                  Leads in DB after this job: <strong>{selectedJob.report.final_counts.leads.toLocaleString()}</strong>
                </div>
              )}

              {/* Live logs */}
              <div>
                <p className="text-xs text-slate-400 mb-1">Live logs ({(selectedJob.logs || []).length})</p>
                <pre
                  className="bg-black/60 rounded-md p-3 text-[11px] font-mono leading-relaxed text-slate-300 max-h-72 overflow-y-auto"
                  data-testid="job-logs"
                >
                  {(selectedJob.logs || []).join("\n") || "(no output yet)"}
                  <div ref={logEndRef} />
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Job history */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-900">Recent imports</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
            ) : jobs.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                No imports yet. Start with <strong>Analyze</strong> to inspect your file safely.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">When</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Phase</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">File</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">By</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Progress</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => (
                      <tr
                        key={j.job_id}
                        className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                        onClick={() => fetchJobDetail(j.job_id)}
                        data-testid={`job-row-${j.job_id}`}
                      >
                        <td className="py-3 px-4 text-xs text-slate-600 whitespace-nowrap">{fmtTime(j.created_at)}</td>
                        <td className="py-3 px-4">
                          <span className="text-xs font-medium text-slate-900">{j.phase}</span>
                        </td>
                        <td className="py-3 px-4"><StatusBadge status={j.status} /></td>
                        <td className="py-3 px-4 text-xs text-slate-600 truncate max-w-[200px]" title={j.file_name}>
                          <FileSpreadsheet className="w-3 h-3 inline mr-1 text-emerald-600" />
                          {j.file_name || "—"}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-600">{j.created_by?.name || "—"}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full ${j.status === "failed" ? "bg-red-500" : j.status === "completed" ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${j.progress_pct ?? 0}%` }} />
                            </div>
                            <span className="text-[11px] text-slate-500">{j.progress_pct ?? 0}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-400">
                          <ChevronRight className="w-4 h-4" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Phase confirmation dialog */}
        <Dialog open={!!openPhase} onOpenChange={(o) => { if (!o) closePhaseDialog(); }}>
          <DialogContent className="max-w-md" data-testid="phase-dialog">
            {openPhase && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <openPhase.icon className="w-4 h-4" />
                    {openPhase.label}
                  </DialogTitle>
                  <DialogDescription className="text-xs leading-relaxed">
                    {openPhase.desc}
                  </DialogDescription>
                </DialogHeader>

                {openPhase.needsFile && (
                  <div className="space-y-2">
                    <Label className="text-xs">Excel file (.xlsx)</Label>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      data-testid="import-file-input"
                    />
                    {file && (
                      <p className="text-xs text-slate-500">
                        Selected: <strong>{file.name}</strong> · {fmtBytes(file.size)}
                      </p>
                    )}
                  </div>
                )}

                {(openPhase.key === "import" || openPhase.key === "dry-run") && entityType === "leads" && (
                  <label className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skipExisting}
                      onChange={(e) => setSkipExisting(e.target.checked)}
                      className="mt-0.5 cursor-pointer"
                      data-testid="skip-existing-checkbox"
                    />
                    <div className="text-xs text-amber-900">
                      <p className="font-medium">Skip rows already in DB (fast re-run)</p>
                      <p className="mt-0.5 text-amber-700 leading-relaxed">
                        Skip every row whose <code className="bg-white px-1 rounded">Opportunity Id</code> is already saved in CRM.
                        Only phone-backfills + fresh inserts run. Cuts re-run time dramatically after a partial/failed import.
                      </p>
                    </div>
                  </label>
                )}

                {openPhase.key === "wipe-and-import" && (
                  <div className="space-y-2 rounded-md bg-red-50 border border-red-200 p-3">
                    <p className="text-xs text-red-700 font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      This will permanently delete all leads, interactions, tasks, site visits, communications, and scoring history.
                    </p>
                    <Label className="text-xs text-red-700">Type <code className="bg-white px-1 py-0.5 rounded font-mono">WIPE</code> to confirm</Label>
                    <Input
                      value={wipeConfirm}
                      onChange={(e) => setWipeConfirm(e.target.value)}
                      placeholder="WIPE"
                      className="border-red-300 focus:border-red-500"
                      data-testid="wipe-confirm-input"
                    />
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={closePhaseDialog} disabled={submitting}>Cancel</Button>
                  <Button
                    onClick={submitJob}
                    disabled={submitting || (openPhase.needsFile && !file) || (openPhase.key === "wipe-and-import" && wipeConfirm !== "WIPE")}
                    className={openPhase.color}
                    data-testid="phase-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    {submitting ? "Starting…" : `Start ${openPhase.label}`}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
